import {
  useSalesServiceSalesInvoicesBrokerPayCreate,
  useSalesServiceSalesInvoicesSendPaymentLinkCreate,
  useSalesServiceSalesInvoicesStaffPayCreate,
  useSalesServiceSalesOrdersCheckoutCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesOrdersPartialUpdate
} from '@insurance/api/queries'
import {
  InvoiceDetail,
  OrderDetail,
  OrderInspectionStatus,
  OrderModify,
  PatchedOrderEdit,
  PaymentTypeStaff,
  PaymentTypeStaffBroker,
  Status9e7Enum
} from '@insurance/api/requests'
import {
  PayBrokerButton,
  PaymentExtendType,
  PaymentValue,
  PayStaffButton
} from '@insurance/components'
import { useAppNavigate, useModifyPayment } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { useSessionContext } from '@shared/hooks'
import {
  getFormatCurrency,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Modal } from 'antd'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

const { confirm } = Modal

const stepKey = { name: 'step' }
const priceKey = { name: 'price' }
const orderKey = { name: 'order_id' }
const vehicleDataProps = { name: 'vehicle_data' }

type NormalizeOrderDataResult = { id: number; requestBody: PatchedOrderEdit }
type NormalizeModifyOrderDataResult = { id: number; requestBody: OrderModify }

type MiddleStepConfig = {
  label?: string
  isLoading?: boolean
  disabled?: boolean
  onClick: (onSuccess: () => void) => void | Promise<void>
}

type ModifyConfig = {
  order?: OrderDetail
  normalizeOrderData: (
    values: Record<string, unknown>
  ) => NormalizeModifyOrderDataResult | Promise<NormalizeModifyOrderDataResult>
}

type Props = {
  steps: number
  onReadyToSaveData?: () => void
  removeFromStorage: () => void
  handleCalculate: (onSuccess: () => void) => Promise<void>
  isCalculatingLoading: boolean
  normalizeOrderData: (
    values: Record<string, unknown>
  ) => NormalizeOrderDataResult | Promise<NormalizeOrderDataResult>
  disabledPay?: boolean
  errorUnwrapKeys?: string[]
  isLegalEntity?: boolean
  middleStep?: MiddleStepConfig
  modify?: ModifyConfig
  extraActions?: ReactNode
  keepDraftOrderOnPrevStep?: boolean
  onPrevStep?: (step: number) => void
}

export const InsurancePaymentControls = ({
  steps,
  onReadyToSaveData,
  removeFromStorage,
  handleCalculate,
  isCalculatingLoading,
  normalizeOrderData,
  disabledPay,
  errorUnwrapKeys,
  isLegalEntity,
  middleStep,
  modify,
  extraActions,
  keepDraftOrderOnPrevStep,
  onPrevStep
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { message } = App.useApp()
  const { broker } = useSessionContext()
  const navigate = useAppNavigate()

  const step = Form.useWatch(stepKey.name, { preserve: true })
  const price = Form.useWatch(priceKey.name, { preserve: true })

  const isModifying = !!modify?.order?.id

  const hasPrevStep = step > 0
  const isCalculateStep = step === 0
  const isMiddleStep = !!middleStep && step === 1 && steps > 2
  const isPayStep = step === steps - 1

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance, { unwrapKeys: errorUnwrapKeys })
  }

  const handleChangeStep = (newStep: number) => {
    formInstance.setFieldValue(stepKey.name, newStep)
    onReadyToSaveData?.()
  }

  const handlePrevStep = () => {
    const prevStep = step - 1
    handleChangeStep(prevStep)
    onPrevStep?.(step)
    if (!prevStep) {
      formInstance.setFieldValue(priceKey.name, undefined)
      if (!keepDraftOrderOnPrevStep) {
        formInstance.setFieldValue(orderKey.name, undefined)
        formInstance.setFieldValue(vehicleDataProps.name, undefined)
      }
    }
  }

  const handleCalculateClick = () => {
    handleCalculate(() => handleChangeStep(step + 1))
  }

  const handleMiddleStepClick = () => {
    middleStep?.onClick(() => handleChangeStep(step + 1))
  }

  const handleSuccessPayment = () => {
    removeFromStorage()
    message.success(
      t(
        isModifying
          ? 'insurance_modified_successfully'
          : 'insurance_created_successfully'
      )
    )
  }

  const { mutate: payBroker, isPending: isBrokerPayLoading } =
    useSalesServiceSalesInvoicesBrokerPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: handleFinishStepError
    })

  const { mutate: pay, isPending: isPayLoading } =
    useSalesServiceSalesInvoicesStaffPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: (error, variables) => {
        handleFinishStepError(error)

        const isPayCash =
          variables?.requestBody?.payment_type === PaymentTypeStaff.CASH
        const errorStatus = (error as { status?: number })?.status

        if (isPayCash && errorStatus !== 500) {
          confirm({
            title: t('error_payment'),
            content: t('error_payment_confirmation'),
            okText: t('pay_manually'),
            onOk: () => {
              pay({
                id: variables.id,
                requestBody: { ...variables.requestBody, force_manual: true }
              })
            }
          })
        }
      }
    })

  const { mutate: checkoutOrder, isPending: isCheckoutLoading } =
    useSalesServiceSalesOrdersCheckoutCreate({
      onError: handleFinishStepError
    })

  const { mutate: paymentLink, isPending: isPaymentLinkLoading } =
    useSalesServiceSalesInvoicesSendPaymentLinkCreate({
      onSuccess: handleSuccessPayment,
      onError: handleFinishStepError
    })

  const { mutate: updateOrder, isPending: isInitOrderLoading } =
    useSalesServiceSalesOrdersPartialUpdate({
      onError: handleFinishStepError
    })

  const { mutate: initModifyOrder, isPending: isInitModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onError: handleFinishStepError
    })

  const { onFinishPay, isPaymentLoading: isModifyPaymentLoading } =
    useModifyPayment({ formInstance })

  const handleInitBrokerOrder = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return
      payBroker({ id: invoice.id })
    }

    const requestData = replaceEmptyWithUndefined(
      await normalizeOrderData(values)
    )

    updateOrder(
      {
        ...requestData,
        requestBody: {
          ...requestData.requestBody,
          status: Status9e7Enum.DATA_ENTRY,
          stage: 0
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder(
            {
              id,
              requestBody: {
                payment_type: PaymentTypeStaffBroker.BROKER_SETTLEMENT
              }
            },
            { onSuccess: handleSuccess, onError: handleFinishStepError }
          )
      }
    )
  }

  const handleInitStaffOrder = async (paymentValue: PaymentValue) => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return

      if (paymentValue.paymentType !== PaymentExtendType.ONLINE_PAYMENT)
        pay({
          id: invoice.id,
          requestBody: {
            payment_type: paymentValue.paymentType,
            cash_box: paymentValue.cashBox
          }
        })

      if (paymentValue.paymentType === PaymentExtendType.ONLINE_PAYMENT)
        paymentLink(
          { id: invoice.id },
          {
            onSuccess: () => {
              navigate(
                invoice.order.id
                  ? paths.orders.details(invoice.order.id)
                  : paths.salesPanel.root
              )
            }
          }
        )
    }

    if (modify && isModifying) {
      const requestData = await modify.normalizeOrderData(values)

      initModifyOrder(
        {
          id: requestData.id,
          requestBody: {
            ...requestData.requestBody,
            status: OrderInspectionStatus.OFFER_READY
          }
        },
        {
          onSuccess: (order) => onFinishPay(order.id, handleSuccess)
        }
      )
      return
    }

    const requestData = replaceEmptyWithUndefined(
      await normalizeOrderData(values)
    )

    updateOrder(
      {
        ...requestData,
        requestBody: {
          ...requestData.requestBody,
          status: Status9e7Enum.DATA_ENTRY,
          stage: 0
        }
      },
      {
        onSuccess: ({ id }) =>
          checkoutOrder(
            {
              id,
              requestBody: {
                credit: replaceEmptyWithUndefined(values.credit),
                payment_type:
                  paymentValue.paymentType as unknown as PaymentTypeStaffBroker
              }
            },
            { onSuccess: handleSuccess }
          )
      }
    )
  }

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button color="primary" variant="outlined" onClick={handlePrevStep}>
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={isCalculatingLoading}
          onClick={handleCalculateClick}
        >
          {t('calculate')}
        </Button>
      )}

      {isMiddleStep && (
        <Button
          type="primary"
          disabled={middleStep?.disabled}
          loading={middleStep?.isLoading}
          onClick={handleMiddleStepClick}
        >
          {middleStep?.label ?? t('next_step')}
        </Button>
      )}

      {isPayStep && !broker && (
        <PayStaffButton
          price={getFormatCurrency({ amount: price?.amount })}
          isLegalEntity={isLegalEntity}
          loading={
            isInitOrderLoading ||
            isPayLoading ||
            isPaymentLinkLoading ||
            isCheckoutLoading ||
            isInitModifyOrderLoading ||
            isModifyPaymentLoading
          }
          disabled={disabledPay}
          onPay={handleInitStaffOrder}
        />
      )}

      {isPayStep && broker && (
        <PayBrokerButton
          loading={
            isInitOrderLoading || isBrokerPayLoading || isCheckoutLoading
          }
          disabledPay={disabledPay}
          onPay={handleInitBrokerOrder}
        />
      )}

      {isPayStep && extraActions}
    </Flex>
  )
}
